import * as THREE from 'three';
import { createRefinedPhotoStand, createDeskShadow } from './RefinedPhotoStand.js';
const BINDER_CENTER = new THREE.Vector3(1836.2921118736267, -566.5533542633057, -506.49630546569824);
const PHOTO_STAND_SCALE = 258;
const PHOTO_STAND_YAW = -0.16;
const DESK_SURFACE_Y = -451.9558131694794;
// Flat top vertices in environment.glb, multiplied by the existing room scale.
const DESK_TOP = { minX: -3587.436962, maxX: 2480.951071, minZ: -1146.457779, maxZ: 1638.677466 };
export function removeOriginalBinders(decorModel) {
    for (const name of ['binder_1', 'binder_2']) {
        const binder = decorModel.getObjectByName(name);
        if (binder)
            binder.visible = false;
    }
}
export function createPhotoStandModel(sourceTexture) {
    const stand = createRefinedPhotoStand({ photoTexture: sourceTexture, includeShadow: false });
    stand.name = 'PhotoStand';
    // Reuse the existing flower's world key light (-3,6,5) aimed at (0,1.8,0).
    // Convert to the stand's rotated local space; no extra lights or renderer changes.
    const localLight = new THREE.Vector3(-3, 4.2, 5)
        .applyAxisAngle(new THREE.Vector3(0, 1, 0), -PHOTO_STAND_YAW);
    stand.add(createDeskShadow(stand, localLight));
    return stand;
}
// Clip each receiver triangle in world space and interpolate its UVs. The desk
// ends just behind the stand: an unclipped shadow would hang over that edge.
function clipShadowToDesk(shadow) {
    shadow.updateWorldMatrix(true, false);
    const inverse = shadow.matrixWorld.clone().invert();
    const original = shadow.geometry;
    const positions = original.getAttribute('position'), uvs = original.getAttribute('uv');
    const indices = original.index;
    const vertices = [], textureUVs = [];
    const boundaries = [
        ['x', DESK_TOP.minX + 1, 1], ['x', DESK_TOP.maxX - 1, -1],
        ['z', DESK_TOP.minZ + 1, 1], ['z', DESK_TOP.maxZ - 1, -1],
    ];
    const count = indices ? indices.count : positions.count;
    for (let i = 0; i < count; i += 3) {
        let polygon = [0, 1, 2].map(offset => {
            const index = indices ? indices.getX(i + offset) : i + offset;
            return {
                position: new THREE.Vector3().fromBufferAttribute(positions, index).applyMatrix4(shadow.matrixWorld),
                uv: new THREE.Vector2(uvs.getX(index), uvs.getY(index)),
            };
        });
        for (const [axis, boundary, direction] of boundaries) {
            const clipped = [];
            for (let k = 0; k < polygon.length; k++) {
                const a = polygon[k], b = polygon[(k + 1) % polygon.length];
                const aInside = direction * (a.position[axis] - boundary) >= 0;
                const bInside = direction * (b.position[axis] - boundary) >= 0;
                if (aInside)
                    clipped.push(a);
                if (aInside !== bInside) {
                    const t = (boundary - a.position[axis]) / (b.position[axis] - a.position[axis]);
                    clipped.push({ position: a.position.clone().lerp(b.position, t), uv: a.uv.clone().lerp(b.uv, t) });
                }
            }
            polygon = clipped;
        }
        for (let k = 1; k < polygon.length - 1; k++) {
            for (const vertex of [polygon[0], polygon[k], polygon[k + 1]]) {
                vertices.push(...vertex.position.clone().applyMatrix4(inverse).toArray());
                textureUVs.push(...vertex.uv.toArray());
            }
        }
    }
    const geometry = new THREE.BufferGeometry();
    geometry.setAttribute('position', new THREE.Float32BufferAttribute(vertices, 3));
    geometry.setAttribute('uv', new THREE.Float32BufferAttribute(textureUVs, 2));
    geometry.computeVertexNormals();
    shadow.geometry = geometry;
    original.dispose();
}
export function placePhotoStandAtBinderLocation(stand) {
    // Ground only physical geometry, never the projected shadow receiver.
    const shadow = stand.getObjectByName('DeskShadow');
    if (shadow)
        stand.remove(shadow);
    stand.scale.setScalar(PHOTO_STAND_SCALE);
    stand.rotation.y = PHOTO_STAND_YAW;
    stand.position.copy(BINDER_CENTER);
    stand.updateMatrixWorld(true);
    const bounds = new THREE.Box3().setFromObject(stand, true);
    stand.position.y += DESK_SURFACE_Y - bounds.min.y;
    if (shadow) {
        stand.add(shadow);
        stand.updateMatrixWorld(true);
        clipShadowToDesk(shadow);
    }
    return stand;
}
export default class PhotoStand {
    constructor(context) {
        this.scene = context.scene;
        removeOriginalBinders(context.decorModel);
        this.model = placePhotoStandAtBinderLocation(createPhotoStandModel(context.texture));
        this.scene.add(this.model);
    }
}
